import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { parseCookies } from 'nookies'
import { Cookies, ICartSelection, StorageKey } from '@/client/types'
import {
  addCartItem,
  addQuoteItemToCart,
  checkoutCart,
  deleteCartItem,
  getCartItems,
  replaceCartItem,
  updateCart,
  updateCartItem,
  USER_DISABLED_ERROR,
  USER_SESSION_INVALID_ERROR
} from '@/infra/services'
import { IMutateUpdateCart, IMutateUpdateCartErrorResponse, IMutateUpdateCartItem } from './types'

export const useGetCartItems = () => {
  const cookies = parseCookies()
  const cartId = cookies[Cookies.CART_ID]
  const { data, isLoading, isFetching, isInitialLoading, refetch, isRefetching, remove } = useQuery({
    queryKey: [StorageKey.GET_CART],
    queryFn: () => getCartItems(cartId),
    enabled: !!cartId
  })

  if (!cartId) {
    return {
      isLoading,
      isFetching,
      isInitialLoading,
      refetch,
      isRefetching,
      remove,
      data: {
        cart_items: [],
        quantity_items: 0
      }
    }
  }
  return {
    isLoading,
    isFetching,
    isInitialLoading,
    refetch,
    isRefetching,
    remove,
    data: {
      ...data,
      quantity_items: data?.cart_items?.length
    }
  }
}

export const useMutateUpdateCart = () => {
  const cookies = parseCookies()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ payload }: IMutateUpdateCart) => updateCart(cookies[Cookies.CART_ID], payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [StorageKey.GET_CART] })
    },
    onError: (error: IMutateUpdateCartErrorResponse) => error
  })
}

export const useMutateAddCartItem = (locale: string) => {
  const queryClient = useQueryClient()
  const cookies = parseCookies()

  return useMutation({
    mutationFn: (item: ICartSelection) => addCartItem(item, locale, cookies[Cookies.CART_ID]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [StorageKey.GET_CART] })
    }
  })
}

export const useMutateAddQuoteItem = () => {
  const queryClient = useQueryClient()
  const cookies = parseCookies()

  return useMutation({
    mutationFn: (quoteItem: string) => addQuoteItemToCart(quoteItem, cookies[Cookies.CART_ID]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [StorageKey.GET_CART] })
    }
  })
}

export const useMutateUpdateCartItem = () => {
  const queryClient = useQueryClient()
  const cookies = parseCookies()

  return useMutation({
    mutationFn: ({ cartItemId, payload }: IMutateUpdateCartItem) =>
      updateCartItem(cookies[Cookies.CART_ID], cartItemId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [StorageKey.GET_CART] })
    }
  })
}

export const useMutateReplaceCartItem = (locale: string) => {
  const queryClient = useQueryClient()
  const cookies = parseCookies()

  return useMutation({
    mutationFn: ({ cartItemId, payload }: IMutateUpdateCartItem) =>
      replaceCartItem(cookies[Cookies.CART_ID], cartItemId, payload, locale),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [StorageKey.GET_CART] })
    }
  })
}

export const useAddQuoteItem = () => {
  const queryClient = useQueryClient()
  const cookies = parseCookies()

  return useMutation({
    mutationFn: (quoteItem: string) => addQuoteItemToCart(quoteItem, cookies[Cookies.CART_ID]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [StorageKey.GET_CART] })
    }
  })
}

export const useMutateDeleteCartItem = () => {
  const queryClient = useQueryClient()
  const cookies = parseCookies()

  return useMutation({
    mutationFn: (cartItemId: string) => deleteCartItem(cookies[Cookies.CART_ID], cartItemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [StorageKey.GET_CART] })
    }
  })
}

export const useGetCheckoutCart = () => {
  const cookies = parseCookies()
  const { data, error, refetch, isError, isLoading, isInitialLoading, isSuccess } = useQuery({
    queryKey: [StorageKey.CHECKOUT_CART],
    queryFn: () => checkoutCart(cookies[Cookies.CART_ID]),
    enabled: false,
    // Never retry a checkout: a failed response doesn't guarantee no order was created
    retry: false,
    // Drop the result as soon as the payment step unmounts, otherwise a stale error
    // (e.g. USER_SESSION_INVALID) is replayed after the user logged in again
    cacheTime: 0
  })

  return {
    isLoading,
    refetch,
    isError,
    isInitialLoading,
    redirect_url: data?.redirect_url,
    isSuccess,
    isUserDisabled: error instanceof Error && error.message === USER_DISABLED_ERROR,
    isUserSessionInvalid: error instanceof Error && error.message === USER_SESSION_INVALID_ERROR
  }
}
